import React from 'react';
import { Box, Container, Typography, Card, CardContent, Divider, Stack, Alert } from '@mui/material';
import { WarningAmber, Gavel, Security, AssignmentTurnedIn } from '@mui/icons-material';
import PublicNavbar from '../components/common/PublicNavbar';
import PublicFooter from '../components/common/PublicFooter';

export default function TermsPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#060d17', color: '#f8fafc' }}>
      <PublicNavbar />

      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '16px',
                bgcolor: 'rgba(37, 99, 235, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Gavel sx={{ color: '#60a5fa', fontSize: 32 }} />
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
              Terms & Conditions
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Effective Date: January 1, 2026 • Version 1.0
            </Typography>
          </Box>

          {/* High Priority Risk Alert */}
          <Alert
            severity="warning"
            icon={<WarningAmber sx={{ color: '#fbbf24' }} />}
            sx={{
              mb: 5,
              bgcolor: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#fef3c7',
              borderRadius: 3,
              '& .MuiAlert-message': { fontSize: '0.88rem', lineHeight: 1.6 },
            }}
          >
            <strong>IMPORTANT LEGAL NOTICE & MARKET RISK DISCLOSURE:</strong> By registering, subscribing, or using our Telegram trading notification bot, you explicitly confirm that you have read, understood, and agreed to all provisions outlined herein. Stock and derivatives trading involves severe financial risks.
          </Alert>

          {/* Terms Content Container */}
          <Card
            sx={{
              bgcolor: 'rgba(15, 23, 42, 0.7)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 4,
              p: { xs: 2, sm: 4 },
              lineHeight: 1.8,
            }}
          >
            <CardContent>
              <Stack spacing={4}>
                {/* 1. Nature of Service */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    1. Nature of Service & Scope
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    AlphaTrading Alerts provides an automated algorithmic market notification service delivered electronically via the Telegram messaging application. The monthly fee of <strong>₹1,000</strong> grants a single subscriber access to real-time notification feeds for thirty (30) calendar days.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    This service is purely informational and algorithmic. We do not provide custody, brokerage services, portfolio management, or trade execution on your personal exchange account.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 2. Regulatory & SEBI Disclaimer */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    2. Regulatory Status (Not SEBI Registered)
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    The service providers, creators, and platform operators are <strong>NOT registered with the Securities and Exchange Board of India (SEBI)</strong> as Investment Advisors (IA), Research Analysts (RA), or Portfolio Managers (PMS).
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    All notifications, technical price levels (entries, targets, stop-losses), and algorithmic alerts are generated automatically through computer software for educational and analytical study only. Nothing transmitted constitutes personalized investment advice or an invitation to trade.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 3. Market Risk & No Profit Guarantees */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    3. Market Risk & Absolute Absence of Guarantees
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Trading in Indian equities, intraday equities, and especially Futures & Options (F&O) derivatives carries an exceptionally high level of risk and may result in the total loss of your trading capital. According to SEBI studies, 9 out of 10 individual traders in the equity F&O segment incur net financial losses.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    <strong>No Profit Warranties:</strong> Any slogans, past performance records, simulations, backtests, or user testimonials do not assure or guarantee future financial returns. You acknowledge that profits are never guaranteed, and you alone bear 100% of the financial consequences of any trade you decide to execute.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 4. Subscription, Pricing & Billing */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    4. Subscription, Pricing & Billing
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Subscribers agree to pay a recurring or manual renewal fee of ₹1,000 per month. Subscriptions are activated immediately upon successful payment verification through authorized payment gateways (Razorpay). Access is valid for 30 consecutive days from the date of activation.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 5. Intellectual Property & Anti-Piracy */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    5. Intellectual Property & Redistribution Ban
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    All notifications, algorithm structures, bot text, logos, and materials are proprietary intellectual property. Subscriptions are strictly for single, individual personal use.
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
                    You are strictly prohibited from forwarding, broadcasting, copying, reselling, or redistributing signals into public or private Telegram channels, WhatsApp groups, Discord servers, websites, or social media. Violation results in immediate, permanent ban without refund and legal prosecution under applicable intellectual property laws.
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 6. Limitation of Liability */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    6. Limitation of Liability & Technical Outages
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    Under no circumstances shall the platform operators, developers, or affiliates be liable for any direct, indirect, incidental, punitive, or consequential damages resulting from:
                  </Typography>
                  <Box component="ul" sx={{ color: '#cbd5e1', pl: 3, mt: 1, '& li': { mb: 0.5 } }}>
                    <li>Any trading losses, bad executions, or missed price levels incurred by you.</li>
                    <li>Downtime, delays, or service interruptions of Telegram, internet service providers, or stock exchanges.</li>
                    <li>Slippage between notification timestamps and personal execution times.</li>
                    <li>Inaccuracies or delays in automated data feeds.</li>
                  </Box>
                </Box>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* 7. Governing Law */}
                <Box>
                  <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 700, mb: 1 }}>
                    7. Governing Law & Jurisdiction
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                    These Terms and Conditions shall be governed by and construed in accordance with the laws of India. Any disputes arising in connection with these terms shall be subject to the exclusive jurisdiction of the competent courts in India.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
}
